// LangSeo.js
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

export function SEO({ page, image }) {
  const { t, i18n } = useTranslation("global");
  const location = useLocation();
  const lang = i18n.language;

  const baseUrl = "https://salumidelnorcino.com";
  const currentPath = location.pathname;

  // Function to get the canonical URL based on current language
  const getCanonicalUrl = () => {
    if (lang === "it") {
      // Italian is the default language, so we use the base path
      // Strip "/it" if it exists in the path
      return `${baseUrl}${currentPath.replace(/^\/it/, "")}`;
    } else {
      // For other languages (e.g., English), include the language code
      // Use the path as is, if it doesn't have language prefix add it
      if (currentPath.startsWith(`/${lang}`)) {
        return `${baseUrl}${currentPath}`;
      }
      return `${baseUrl}/${lang}${currentPath}`;
    }
  };

  // Function to get alternate URLs for language versions
  const getAlternateUrl = (targetLang) => {
    // First get the path without any language prefix
    let basePath = currentPath.replace(/^\/(it|en)/, "");

    // Then add the appropriate language prefix
    if (targetLang === "it") {
      // Italian is default, so no prefix needed
      return `${baseUrl}${basePath}`;
    } else {
      return `${baseUrl}/${targetLang}${basePath}`;
    }
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{t(`seo.${page}.title`)}</title>
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Salumi del Norcino" />
      <meta name="publisher" content="Salumi del Norcino" />
      <meta name="description" content={t(`seo.${page}.description`)} />
      <meta name="keywords" content={t(`seo.${page}.keywords`)} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={t(`seo.${page}.title`)} />
      <meta property="og:description" content={t(`seo.${page}.description`)} />
      <meta property="og:url" content={getCanonicalUrl()} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={lang === "it" ? "it_IT" : "en_US"} />
      {image && <meta property="og:image" content={`${baseUrl}${image}`} />}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t(`seo.${page}.title`)} />
      <meta name="twitter:description" content={t(`seo.${page}.description`)} />
      {image && <meta name="twitter:image" content={`${baseUrl}${image}`} />}

      {/* Alternate Languages */}
      <link rel="alternate" hreflang="it" href={getAlternateUrl("it")} />
      <link rel="alternate" hreflang="en" href={getAlternateUrl("en")} />
      <link rel="alternate" hreflang="x-default" href={getAlternateUrl("it")} />
      <link rel="canonical" href={getCanonicalUrl()} />
    </Helmet>
  );
}
